/** US survey foot–based mile definition used for road-style conversions. */
export const METERS_PER_MILE = 1609.344

export type DistanceInputUnit = 'miles' | 'km' | 'm'

export function metersToMiles(m: number): number {
  return m / METERS_PER_MILE
}

export function milesToMeters(miles: number): number {
  return miles * METERS_PER_MILE
}

export function metersToKm(m: number): number {
  return m / 1000
}

export function kmToMeters(km: number): number {
  return km * 1000
}

export function metersToDisplay(m: number, unit: DistanceInputUnit): number {
  if (unit === 'miles') return metersToMiles(m)
  if (unit === 'km') return metersToKm(m)
  return m
}

/**
 * Rounds the **display-space** value for inputs: hundredths for miles/km, whole metres for `m`.
 */
export function roundDisplayUnit(value: number, unit: DistanceInputUnit): number {
  if (!Number.isFinite(value)) return value
  if (unit === 'm') return Math.round(value)
  return Math.round(value * 100) / 100
}

export function displayToMeters(value: number, unit: DistanceInputUnit): number {
  if (unit === 'miles') return milesToMeters(value)
  if (unit === 'km') return kmToMeters(value)
  return value
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

/** Round `value` to the nearest multiple of `step` (step > 0). */
export function roundToStep(value: number, step: number): number {
  if (!Number.isFinite(step) || step <= 0) return value
  const inv = 1 / step
  return Math.round(value * inv) / inv
}

export function roundMetersToStep(meters: number, stepMeters: number): number {
  return roundToStep(meters, stepMeters)
}

/**
 * Secondary “≈ …” line: same distance in metric wording.
 * - `alongsideImperialInput`: user typed miles → show metres, or km if ≥ 1000 m.
 * - `alongsideMetricInput`: user already uses km/m → show only metres to avoid repeating km.
 */
export function formatMetresHintLine(
  metres: number,
  mode: 'alongsideImperialInput' | 'alongsideMetricInput'
): string {
  if (!Number.isFinite(metres)) return ''
  if (mode === 'alongsideMetricInput') {
    return `${Math.round(metres)} m`
  }
  if (metres >= 1000) {
    return `${(metres / 1000).toFixed(2)} km`
  }
  return `${Math.round(metres)} m`
}

export function unitShortLabel(unit: DistanceInputUnit): string {
  if (unit === 'miles') return 'mi'
  if (unit === 'km') return 'km'
  return 'm'
}
